import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, FormControlLabel, Grid, GridRow, Modal, ModalFooter, Radio, RadioGroup, Select, Spacer, TextField, Typography, useSnackbar } from '@emburse/embark-core';
import { SupportIcon } from '@emburse/embark-icons';
import { IconPopover } from '../../components';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { createCompanyACHAccount, updateCompanyACHAccount } from '../../api/companyACHAccount';
import { ForteApplicationStatus, getForteApplicationStatus } from '../../api/forteApplication';
import { CompanyACHAccount, PaymentPartner } from '../../interfaces/CompanyACHAccount';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { logger } from '../../util/logger';
import { isEINValid } from '../../util/formValidation';
import useAccountConfig from '../../hooks/useAccountConfig';
var PrefState;
(function (PrefState) {
    PrefState["On"] = "1";
    PrefState["Off"] = "0";
})(PrefState || (PrefState = {}));
function CorpACHConfig({ companyACHAccount, payrollExpertsID, payrollExpertsRun }) {
    const { accountConfig } = useAccountConfig();
    useTranslation();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [formCompanyACHAccount, setFormCompanyACHAccount] = useState(new CompanyACHAccount(companyACHAccount || {}));
    const [formPayrollExpertsRun, setFormPayrollExpertsRun] = useState({
        checked: false,
        value: ''
    });
    const [applicationStatus, setApplicationStatus] = useState(null);
    // original serialized
    const [originalAchAccount, setOriginalAchAccount] = useState(JSON.stringify(companyACHAccount));
    const [originalPayrollExpertsRun, setOriginalPayrollExpertsRun] = useState(payrollExpertsRun);
    const [modal, setModal] = useState({
        isOpen: false
    });
    const isFraudTeam = accountConfig.UIFlags.CanUseInternalToolsFraud;
    const isSuperAdmin = accountConfig.IsAdmin;
    const showForteOption = accountConfig.UIFlags.EnableForte &&
        !((companyACHAccount.PaymentPartner === PaymentPartner.Convera ||
            companyACHAccount.PaymentPartner === PaymentPartner.PayrollExperts) &&
            applicationStatus?.Status !== ForteApplicationStatus.Enrolled);
    const { enqueueSnackbar } = useSnackbar();
    const companyInfo = $t(`Confirm all company information is accurate. The Payroll Experts ID is auto-generated 
                    by Certify. It is included in payment data to identify a specific company, and should 
                    be provided to Payroll Experts for their configuration prior to payments being submitted.`);
    const processingInfo = $t(`Selecting 48 hours will send instructions to Payroll Experts to reimburse the client's 
                        employees/vendors within 48 hours. Please ensure you have received approval from Payroll 
                        Experts before enabling this option.`);
    useEffect(() => {
        setShowEdit(companyACHAccount?.PK > 0);
    }, [companyACHAccount]);
    useEffect(() => {
        let booChecked = false;
        let strValue = '';
        if (originalPayrollExpertsRun?.length > 0) {
            booChecked = originalPayrollExpertsRun === PrefState.On;
        }
        else if (isFraudTeam &&
            !originalAchAccount &&
            formCompanyACHAccount.PaymentPartner === PaymentPartner.PayrollExperts) {
            strValue = PrefState.On;
            booChecked = true;
        }
        setFormPayrollExpertsRun({
            checked: booChecked,
            value: strValue
        });
    }, [originalPayrollExpertsRun, isFraudTeam, originalAchAccount, formCompanyACHAccount]);
    useEffect(() => {
        (async () => {
            try {
                const response = await getForteApplicationStatus();
                setApplicationStatus(response);
            }
            catch (e) {
                enqueueSnackbar($t('Failed to fetch Forte application status'), {
                    variant: 'alert',
                    AlertProps: { severity: 'error' }
                });
            }
        })();
    }, [enqueueSnackbar]);
    /** Form Change Check */
    useEffect(() => {
        const payrollExpertsRunChanges = isFraudTeam &&
            formPayrollExpertsRun.value !== '' &&
            formPayrollExpertsRun.value !== originalPayrollExpertsRun;
        const changesFound = JSON.stringify(formCompanyACHAccount) !== originalAchAccount ||
            payrollExpertsRunChanges;
        setHasUnsavedChanges(changesFound);
    }, [
        formCompanyACHAccount,
        originalAchAccount,
        formPayrollExpertsRun,
        originalPayrollExpertsRun,
        isFraudTeam
    ]);
    useEffect(() => {
        if (formCompanyACHAccount.PaymentPartner === PaymentPartner.Forte &&
            !companyACHAccount?.PK) {
            setFormCompanyACHAccount((prevState) => ({
                ...prevState,
                Inactive: true
            }));
        }
    }, [formCompanyACHAccount.PaymentPartner, companyACHAccount?.PK]);
    const removeInvalidState = (key) => {
        setInvalidFields((prevState) => prevState.filter((x) => x.Key !== key));
    };
    const getValidationResult = (key) => invalidFields.find((x) => x.Key === key);
    /** onChange Handlers */
    const handleSaveClick = async () => {
        if (!hasUnsavedChanges) {
            return;
        }
        let success = false;
        const isNew = !formCompanyACHAccount.PK;
        setModal({
            isOpen: false
        });
        applyLoadingState();
        try {
            if (!formCompanyACHAccount.PK) {
                await createCompanyACHAccount(formCompanyACHAccount, formPayrollExpertsRun.value).then((resp) => {
                    if (resp.Status === 'success') {
                        const companyAchAccountRecordPK = window.parseInt(resp.Data);
                        setFormCompanyACHAccount({
                            ...formCompanyACHAccount,
                            PK: companyAchAccountRecordPK
                        });
                        success = true;
                    }
                    else if (resp.Status === 'reject') {
                        // apply any validation results
                        const data = resp.Data;
                        setInvalidFields(data);
                    }
                });
            }
            else {
                await updateCompanyACHAccount(formCompanyACHAccount, formPayrollExpertsRun.value).then((resp) => {
                    if (resp.Status === 'success') {
                        success = true;
                    }
                    else if (resp.Status === 'reject') {
                        // apply any validation results
                        const data = resp.Data;
                        setInvalidFields(data);
                    }
                });
            }
        }
        catch (message) {
            logger.log(message);
        }
        finally {
            if (success) {
                setOriginalAchAccount(JSON.stringify(formCompanyACHAccount));
                setOriginalPayrollExpertsRun(formPayrollExpertsRun.value || originalPayrollExpertsRun);
            }
            removeLoadingState();
            // Alert on Success/Failure
            enqueueSnackbar(success
                ? $t('Payments configuration updated successfully.')
                : $t('Unable to save update payment configuration. Verify form fields do not have invalid inputs'), { variant: 'alert', AlertProps: { severity: success ? 'success' : 'error' } });
            // reload the page so the payroll experts id is visible. PE ID is
            // derived from the companyAchAccount row guid.
            if (isNew && success) {
                window.location.reload();
            }
        }
    };
    const handleConfirm = async () => {
        if (formCompanyACHAccount &&
            formCompanyACHAccount.PaymentPartner === PaymentPartner.Forte &&
            formCompanyACHAccount.PK < 1) {
            await handleSaveClick();
        }
        else {
            setModal({
                isOpen: true
            });
        }
    };
    const handleModalClose = () => {
        setModal({
            isOpen: false
        });
    };
    // Forte application is considered pending when:
    // 1. Forte is selected as payment partner AND
    // 2. Application status is not enrolled or unsubmitted (could be approved, pending, rejected, etc.)
    // This prevents changes to payment settings while application is being processed
    const isForteApplicationPending = accountConfig.UIFlags.EnableForte &&
        formCompanyACHAccount.PaymentPartner === PaymentPartner.Forte &&
        applicationStatus !== null &&
        applicationStatus.Status !== ForteApplicationStatus.Enrolled &&
        applicationStatus.Status !== ForteApplicationStatus.Unsubmitted;
    // 1. Not enrolled, OR
    // 2. User lacks permissions AND trying to enable
    const isActivateReimbursementsCheckboxDisabled = isForteApplicationPending ||
        (!formCompanyACHAccount.Inactive && !(isFraudTeam || isSuperAdmin));
    const showForteActivateReimbursements = useMemo(() => (isFraudTeam || isSuperAdmin) &&
        formCompanyACHAccount.PaymentPartner === PaymentPartner.Forte &&
        applicationStatus !== null &&
        applicationStatus.Status === ForteApplicationStatus.Enrolled, [applicationStatus, formCompanyACHAccount.PaymentPartner, isFraudTeam, isSuperAdmin]);
    const isSaveButtonDisabled = isForteApplicationPending ||
        !hasUnsavedChanges ||
        !(isFraudTeam || formCompanyACHAccount.PaymentPartner !== PaymentPartner.PayrollExperts
            ? isSuperAdmin
            : false);
    const handlePaymentPartnerChange = (event) => {
        if (isForteApplicationPending) {
            return;
        }
        setFormCompanyACHAccount((prevState) => ({
            ...prevState,
            PaymentPartner: event.target.value
        }));
    };
    const handleAccountTypeChange = (event) => {
        setFormCompanyACHAccount((prevState) => ({
            ...prevState,
            AccountType: event.target.value
        }));
    };
    const handleFundingIntervalChange = (event) => {
        setFormCompanyACHAccount((prevState) => ({
            ...prevState,
            FundingInterval: event.target.value
        }));
    };
    const handleRoutingNumberChange = (event) => {
        removeInvalidState('RoutingNumber');
        setFormCompanyACHAccount((prevState) => ({
            ...prevState,
            RoutingNumber: event.target.value
        }));
    };
    const handleAccountNumberChange = (event) => {
        removeInvalidState('AccountNumber');
        setFormCompanyACHAccount((prevState) => ({
            ...prevState,
            AccountNumber: event.target.value
        }));
    };
    const handleClientNameChange = (event) => {
        setFormCompanyACHAccount((prevState) => ({ ...prevState, ClientName: event.target.value }));
    };
    const handleFederalIDChange = (event) => {
        setFormCompanyACHAccount((prevState) => ({ ...prevState, FederalID: event.target.value }));
    };
    const handleInactiveChange = (event) => {
        setFormCompanyACHAccount((prevState) => ({
            ...prevState,
            Inactive: !event.target.checked
        }));
    };
    const handleFormPayrollExpertsRunChange = (event) => {
        const newPrefValue = event.target.checked ? PrefState.On : PrefState.Off;
        const hasUnsavedPayrollExpertsRunChanges = newPrefValue !== (originalPayrollExpertsRun || PrefState.Off);
        setFormPayrollExpertsRun({
            checked: event.target.checked,
            value: hasUnsavedPayrollExpertsRunChanges ? newPrefValue : ''
        });
    };
    const modalFooter = () => (_jsx(ModalFooter, { children: _jsxs(Grid, { container: true, spacing: 1, direction: "row", justifyContent: "flex-end", alignItems: "center", children: [_jsx(Grid, { item: true, children: _jsx(Button, { variant: "outlined", onClick: handleModalClose, children: $t('Cancel') }) }), _jsx(Grid, { item: true, children: _jsx(Button, { onClick: handleSaveClick, children: $t('Continue') }) })] }) }));
    const modalBody = () => (_jsxs(_Fragment, { children: [formCompanyACHAccount?.PK < 1 && (_jsxs(_Fragment, { children: [_jsx(Typography, { children: $t(`If all other coordination between client, certify team, and payment partner have been completed,
                        saving this configuration will enable payment processing.`) }), _jsx(Spacer, { size: 16 }), _jsx(Typography, { children: $t('Enable payments configuration for this company?') })] })), formCompanyACHAccount?.PK > 0 && (_jsx(Typography, { children: $t(`Modifications to this company's payments configuration will affect all current and
                    future payment batches. Before making changes, verify that any pending payments have been processed
                    and that account information is accurate.`) }))] }));
    return (_jsxs(SingleColumnLayout, { children: [_jsx(Typography, { variant: "h4", children: "Payments Configuration" }), _jsx(Spacer, { size: 32 }), showEdit &&
                isForteApplicationPending &&
                applicationStatus &&
                applicationStatus.Status !== ForteApplicationStatus.Unsubmitted && (_jsxs(_Fragment, { children: [_jsx(Alert, { severity: "info", children: $t('This customer has a pending Forte application. These settings cannot be changed until Forte has finished processing the application.') }), _jsx(Spacer, { size: 16 })] })), !showEdit && (_jsxs(_Fragment, { children: [_jsx(GridRow, { spacing: 2, alignItems: "center", alignContent: "center", children: _jsx(Grid, { item: true, children: _jsx(Alert, { severity: "info", children: $t("This company doesn't have payments configured.") }) }) }), _jsx(Spacer, { size: 48 }), _jsx(GridRow, { spacing: 2, alignItems: "center", alignContent: "center", children: _jsx(Grid, { item: true, children: _jsx(Button, { size: "large", onClick: () => {
                                    setShowEdit(true);
                                }, children: $t('Configure Payments') }) }) })] })), showEdit && (_jsxs(_Fragment, { children: [_jsxs(Card, { children: [_jsx(GridRow, { alignItems: "center", children: _jsx(Grid, { item: true, children: _jsx(Typography, { variant: "subtitle1", children: $t('Payment Partner') }) }) }), _jsx(GridRow, { alignItems: "center", children: _jsx(Grid, { item: true, children: _jsx(Typography, { variant: "body1", children: $t(`Note: For new configurations, additional coordination is needed
                                    between the client company, the Certify team, and the payment partner.`) }) }) }), _jsx(Spacer, { size: 16 }), _jsx(GridRow, { children: _jsx(Grid, { item: true, children: _jsxs(RadioGroup, { name: "PaymentPartner", defaultValue: formCompanyACHAccount.PaymentPartner || 'Convera', onChange: handlePaymentPartnerChange, children: [_jsx(FormControlLabel, { value: "Convera", control: _jsx(Radio, { color: "primary", name: "PaymentPartner", value: "Convera" }), label: "Convera", disabled: isForteApplicationPending }), _jsx(FormControlLabel, { value: "PayrollExperts", control: _jsx(Radio, { color: "primary", name: "PaymentPartner", value: "PayrollExperts" }), label: "Payroll Experts", disabled: isForteApplicationPending }), showForteOption && (_jsx(FormControlLabel, { value: "Forte", control: _jsx(Radio, { color: "primary", name: "PaymentPartner", value: "Forte", "data-testid": "forte-radio" }), label: "Forte", disabled: isForteApplicationPending }))] }) }) })] }), formCompanyACHAccount.PaymentPartner === PaymentPartner.PayrollExperts && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 16 }), _jsxs(Card, { children: [_jsx(Typography, { variant: "subtitle1", children: $t('Bank Account') }), _jsx(Spacer, { size: 16 }), _jsxs(GridRow, { children: [_jsx(Grid, { item: true, children: _jsxs(Select, { value: formCompanyACHAccount.AccountType, helperText: "Should match the account.", id: "AccountType", label: "Account Type", onChange: handleAccountTypeChange, native: true, children: [_jsx("option", { value: "Checking", children: $t('Checking') }), _jsx("option", { value: "Savings", children: $t('Savings') })] }) }), _jsx(Spacer, { direction: "horizontal", size: 16 }), _jsx(Grid, { item: true, children: _jsx(TextField, { helperText: getValidationResult('RoutingNumber')?.Message ||
                                                        $t('Should be 9 digits long'), inputProps: { maxLength: 9 }, label: $t('Origination Routing Number'), value: formCompanyACHAccount.RoutingNumber, id: "RoutingNumber", onChange: handleRoutingNumberChange, style: { width: '238px' }, error: !!getValidationResult('RoutingNumber') }) }), _jsx(Spacer, { direction: "horizontal", size: 16 }), _jsx(Grid, { item: true, children: _jsx(TextField, { helperText: getValidationResult('AccountNumber')?.Message ||
                                                        $t('Should be 6-17 digits long'), inputProps: { maxLength: 17 }, label: $t('Origination Account Number'), value: formCompanyACHAccount.AccountNumber, id: "AccountNumber", onChange: handleAccountNumberChange, style: { width: '238px' }, error: !!getValidationResult('AccountNumber') }) })] })] }), _jsx(Spacer, { size: 16 }), _jsxs(Card, { children: [_jsxs(GridRow, { alignItems: "center", alignContent: "center", children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "subtitle1", children: $t('Company Information') }) }), _jsx(Grid, { item: true, children: _jsx(IconPopover, { title: $t('Company Information'), content: companyInfo, iconUrl: SupportIcon, edge: false }) })] }), _jsx(Spacer, { size: 16 }), _jsxs(GridRow, { children: [_jsx(Grid, { item: true, children: _jsx(TextField, { value: formCompanyACHAccount.ClientName, helperText: $t('Name used on IRS documents.'), id: "ClientName", inputProps: { maxLength: 40 }, label: $t('Legal Name'), onChange: handleClientNameChange, style: { width: '238px' } }) }), _jsx(Spacer, { direction: "horizontal", size: 16 }), _jsx(Grid, { item: true, children: _jsx(TextField, { value: formCompanyACHAccount.FederalID, helperText: $t('Format should be ##-#######.'), id: "FederalID", inputProps: { maxLength: 10 }, label: $t('Federal EIN'), onChange: handleFederalIDChange, style: { width: '238px' }, error: formCompanyACHAccount.FederalID.length > 0 &&
                                                        isEINValid(formCompanyACHAccount.FederalID) ===
                                                            false }) }), payrollExpertsID && (_jsxs(_Fragment, { children: [_jsx(Spacer, { direction: "horizontal", size: 16 }), _jsx(Grid, { item: true, children: _jsx(TextField, { value: payrollExpertsID || '', disabled: true, helperText: $t("Identifies this company's payments."), id: "PayrollExpertsID", label: "Payroll Experts ID", style: { width: '238px' } }) })] }))] })] }), _jsx(Spacer, { size: 16 }), _jsxs(Card, { children: [_jsxs(GridRow, { alignItems: "center", alignContent: "center", children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "subtitle1", children: $t('Processing') }) }), _jsx(Grid, { item: true, children: _jsx(IconPopover, { title: $t('Processing'), content: processingInfo, iconUrl: SupportIcon, edge: false }) })] }), _jsx(Spacer, { size: 16 }), _jsx(GridRow, { children: _jsx(Grid, { item: true, children: _jsxs(Select, { value: formCompanyACHAccount.FundingInterval, id: "FundingInterval", label: $t('Funding Interval'), onChange: handleFundingIntervalChange, native: true, children: [_jsx("option", { value: "NinetySixHours", children: "96 Hours" }), _jsx("option", { value: "FortyEightHours", children: "48 Hours" })] }) }) })] }), _jsx(Spacer, { size: 16 }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { id: "formPayrollExpertsRun", color: "primary", checked: formPayrollExpertsRun.checked, onChange: handleFormPayrollExpertsRunChange }), label: $t('Activate Reimbursements'), disabled: !isFraudTeam, labelPlacement: "end" }), _jsx(Spacer, { size: 16 })] })), showForteActivateReimbursements && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 16 }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { id: "Inactive", color: "primary", checked: !formCompanyACHAccount.Inactive, onChange: handleInactiveChange }), label: $t('Activate Reimbursements'), disabled: isActivateReimbursementsCheckboxDisabled, labelPlacement: "end" }), _jsx(Spacer, { size: 16 })] })), _jsx(Spacer, { size: 16 }), _jsx(GridRow, { children: _jsx(Grid, { item: true, children: _jsx(Alert, { severity: "warning", children: $t(`Before changing payment partners, be sure any queued payments have been processed
                                using the ACH Reimbursement History report, looking at the transaction ID parameter.`) }) }) }), _jsx(Spacer, { size: 16 }), _jsx(GridRow, { children: _jsx(Grid, { item: true, children: _jsx(Button, { size: "large", onClick: handleConfirm, disabled: isSaveButtonDisabled, children: "Save" }) }) })] })), _jsx(Modal, { size: "sm", open: modal.isOpen, modalFooter: modalFooter(), modalHeader: $t('Confirmation'), onClose: () => {
                    setModal({
                        ...modal,
                        isOpen: false
                    });
                }, children: modalBody() })] }));
}
export default CorpACHConfig;
